import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Badge,
  Tab,
  Tabs,
  Alert,
  Button
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Inventory as InventoryIcon,
  Business as BusinessIcon,
  LocalShipping as BatchIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  Refresh as RefreshIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { productionPlantsApi } from '../services/productionPlantsApi';

// Styled components for modern design
const StyledCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#ffffff',
  border: '1px solid #e0e0e0',
  borderRadius: '12px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
    transform: 'translateY(-2px)',
  },
}));

const MetricCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#f8f9fa',
  border: '1px solid #e9ecef',
  borderRadius: '8px',
  padding: theme.spacing(2),
  textAlign: 'center',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: '#ffffff',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  },
}));

const SectionCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#ffffff',
  border: '1px solid #e0e0e0',
  borderRadius: '8px',
  marginBottom: theme.spacing(2),
  overflow: 'hidden',
}));

const BatchCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#f8f9fa',
  border: '1px solid #dee2e6',
  borderRadius: '6px',
  marginBottom: theme.spacing(1),
}));

const StyledChip = styled(Chip)(({ theme, status }) => ({
  fontWeight: 600,
  fontSize: '0.75rem',
  height: '24px',
  backgroundColor: 
    status === 'active' ? '#000000' :
    status === 'pending' ? '#ff9800' :
    status === 'in_progress' ? '#2196f3' :
    status === 'completed' ? '#4caf50' :
    status === 'cancelled' ? '#f44336' :
    '#757575',
  color: '#ffffff',
}));

const ProgressBar = styled(LinearProgress)(({ theme }) => ({
  height: '8px',
  borderRadius: '4px',
  backgroundColor: '#e0e0e0',
  '& .MuiLinearProgress-bar': {
    borderRadius: '4px',
  },
}));

const ProductionPlantInventory = () => {
  const { id } = useParams();
  
  // State management
  const [inventoryData, setInventoryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'table'
  const [error, setError] = useState(null);

  useEffect(() => {
    if (id) {
      fetchInventoryData();
    }
  }, [id]);

  const fetchInventoryData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await productionPlantsApi.getPlantInventory(id);
      setInventoryData(response.data);
    } catch (error) {
      console.error('Error fetching inventory data:', error);
      setError('Failed to fetch inventory data');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const calculateBatchProgress = (batch) => {
    if (!batch.requested_quantity || batch.requested_quantity === 0) return 0;
    return Math.min((batch.produced_quantity / batch.requested_quantity) * 100, 100);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return '#4caf50';
      case 'pending': return '#ff9800';
      case 'in_progress': return '#2196f3';
      case 'completed': return '#4caf50';
      case 'cancelled': return '#f44336';
      case 'on_hold': return '#9e9e9e';
      default: return '#757575';
    }
  };

  const renderOverviewTab = () => {
    if (!inventoryData) return null;

    const { section_summary, batch_summary } = inventoryData;
    const totalItems = section_summary.reduce((sum, section) => sum + (section.unique_items || 0), 0);
    const totalQuantity = section_summary.reduce((sum, section) => sum + (section.total_quantity || 0), 0);
    const totalBatches = batch_summary.length;
    const activeBatches = batch_summary.filter(batch => batch.batch_status === 'in_progress').length;

    return (
      <Box sx={{ p: 3 }}>
        {/* Key Metrics */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard>
              <Box display="flex" alignItems="center" justifyContent="center" gap={1} mb={1}>
                <InventoryIcon sx={{ color: '#000000' }} />
                <Typography variant="h4" fontWeight={700} color="#000000">
                  {totalItems.toLocaleString()}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Unique Items
              </Typography>
            </MetricCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard>
              <Box display="flex" alignItems="center" justifyContent="center" gap={1} mb={1}>
                <TrendingUpIcon sx={{ color: '#2196f3' }} />
                <Typography variant="h4" fontWeight={700} color="#2196f3">
                  {totalQuantity.toLocaleString()}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Total Quantity
              </Typography>
            </MetricCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard>
              <Box display="flex" alignItems="center" justifyContent="center" gap={1} mb={1}>
                <BatchIcon sx={{ color: '#ff9800' }} />
                <Typography variant="h4" fontWeight={700} color="#ff9800">
                  {totalBatches}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Total Batches
              </Typography>
            </MetricCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard>
              <Box display="flex" alignItems="center" justifyContent="center" gap={1} mb={1}>
                <AssessmentIcon sx={{ color: '#4caf50' }} />
                <Typography variant="h4" fontWeight={700} color="#4caf50">
                  {activeBatches}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Active Batches
              </Typography>
            </MetricCard>
          </Grid>
        </Grid>

        {/* Sections Overview */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" fontWeight={600} color="#000000" gutterBottom>
            Sections Overview
          </Typography>
          {section_summary.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
              <BusinessIcon sx={{ fontSize: '3rem', color: '#e0e0e0', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Sections Found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Create sections in this plant to start tracking inventory
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {section_summary.map((section) => (
                <Grid item xs={12} sm={6} md={4} key={section.section_id}>
                  <SectionCard>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <BusinessIcon sx={{ color: '#000000' }} />
                          <Typography variant="h6" fontWeight={600}>
                            {section.section_name}
                          </Typography>
                        </Box>
                        <StyledChip
                          label={section.section_status}
                          status={section.section_status}
                          size="small"
                        />
                      </Box>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={4}>
                          <Typography variant="h5" fontWeight={700} color="#2196f3">
                            {section.unique_items || 0}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Items
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="h5" fontWeight={700} color="#4caf50">
                            {(section.total_quantity || 0).toLocaleString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Quantity
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="h5" fontWeight={700} color="#ff9800">
                            {section.active_batches || 0}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Batches
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </SectionCard>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>

        {/* Batches Overview */}
        <Box>
          <Typography variant="h5" fontWeight={600} color="#000000" gutterBottom>
            Batches Overview
          </Typography>
          {batch_summary.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
              <BatchIcon sx={{ fontSize: '3rem', color: '#e0e0e0', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Batches Found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Create production batches to start tracking batch-specific inventory
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {batch_summary.map((batch) => (
                <Grid item xs={12} sm={6} md={4} key={batch.batch_id}>
                  <BatchCard>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6" fontWeight={600}>
                          {batch.batch_number}
                        </Typography>
                        <StyledChip
                          label={batch.batch_status}
                          status={batch.batch_status}
                          size="small"
                        />
                      </Box>
                      
                      {batch.batch_description && (
                        <Typography variant="body2" color="text.secondary" mb={2}>
                          {batch.batch_description}
                        </Typography>
                      )}
                      
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="body2" fontWeight={600}>
                            {batch.batch_items || 0} Items
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Total Items
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" fontWeight={600}>
                            {(batch.batch_total_quantity || 0).toLocaleString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Total Quantity
                          </Typography>
                        </Grid>
                      </Grid>
                      
                      <Box mt={2}>
                        <Typography variant="caption" color="text.secondary">
                          Sections: {batch.sections_involved || 0}
                        </Typography>
                      </Box>
                    </CardContent>
                  </BatchCard>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Box>
    );
  };

  const renderSectionsTab = () => {
    if (!inventoryData) return null;

    const { detailed_inventory } = inventoryData;
    
    if (!detailed_inventory || detailed_inventory.length === 0) {
      return (
        <Box sx={{ p: 6, textAlign: 'center' }}>
          <BusinessIcon sx={{ fontSize: '4rem', color: '#e0e0e0', mb: 2 }} />
          <Typography variant="h5" color="text.secondary" gutterBottom>
            No Inventory Items Found
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={3}>
            Start by adding items to your production sections to track inventory
          </Typography>
        </Box>
      );
    }
    
    // Group by sections
    const sectionGroups = detailed_inventory.reduce((groups, item) => {
      const sectionKey = `${item.section_id}-${item.section_name}`;
      if (!groups[sectionKey]) {
        groups[sectionKey] = {
          section_id: item.section_id,
          section_name: item.section_name,
          section_order: item.section_order,
          items: []
        };
      }
      groups[sectionKey].items.push(item);
      return groups;
    }, {});

    return (
      <Box sx={{ p: 3 }}>
        {Object.values(sectionGroups)
          .sort((a, b) => a.section_order - b.section_order)
          .map((section) => (
            <Accordion key={section.section_id} sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" gap={2} width="100%">
                  <BusinessIcon sx={{ color: '#000000' }} />
                  <Typography variant="h6" fontWeight={600}>
                    {section.section_name}
                  </Typography>
                  <Badge badgeContent={section.items.length} color="primary" />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                        <TableCell fontWeight={600}>Item</TableCell>
                        <TableCell fontWeight={600}>SKU</TableCell>
                        <TableCell fontWeight={600}>Category</TableCell>
                        <TableCell fontWeight={600}>Batch</TableCell>
                        <TableCell fontWeight={600} align="right">Quantity</TableCell>
                        <TableCell fontWeight={600}>Last Updated</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {section.items.map((item, index) => (
                        <TableRow key={`${item.item_id}-${item.batch_id}-${index}`}>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {item.item_name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {item.sku || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {item.category || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {item.batch_number ? (
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="body2">
                                  {item.batch_number}
                                </Typography>
                                <StyledChip
                                  label={item.batch_status}
                                  status={item.batch_status}
                                  size="small"
                                />
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                General
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={600}>
                              {item.quantity.toLocaleString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {new Date(item.last_updated).toLocaleDateString()}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          ))}
      </Box>
    );
  };

  const renderBatchesTab = () => {
    if (!inventoryData) return null;

    const { detailed_inventory } = inventoryData;
    
    if (!detailed_inventory || detailed_inventory.length === 0) {
      return (
        <Box sx={{ p: 6, textAlign: 'center' }}>
          <BatchIcon sx={{ fontSize: '4rem', color: '#e0e0e0', mb: 2 }} />
          <Typography variant="h5" color="text.secondary" gutterBottom>
            No Batch Inventory Found
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={3}>
            Create production batches and assign items to track batch-specific inventory
          </Typography>
        </Box>
      );
    }
    
    // Group by batches
    const batchGroups = detailed_inventory.reduce((groups, item) => {
      if (!item.batch_id) return groups;
      
      const batchKey = `${item.batch_id}-${item.batch_number}`;
      if (!groups[batchKey]) {
        groups[batchKey] = {
          batch_id: item.batch_id,
          batch_number: item.batch_number,
          batch_status: item.batch_status,
          items: []
        };
      }
      groups[batchKey].items.push(item);
      return groups;
    }, {});

    if (Object.keys(batchGroups).length === 0) {
      return (
        <Box sx={{ p: 6, textAlign: 'center' }}>
          <BatchIcon sx={{ fontSize: '4rem', color: '#e0e0e0', mb: 2 }} />
          <Typography variant="h5" color="text.secondary" gutterBottom>
            No Batch-Specific Inventory
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={3}>
            All inventory items are general inventory. Create production batches to track batch-specific items.
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ p: 3 }}>
        {Object.values(batchGroups).map((batch) => (
          <Accordion key={batch.batch_id} sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" gap={2} width="100%">
                <BatchIcon sx={{ color: '#ff9800' }} />
                <Typography variant="h6" fontWeight={600}>
                  {batch.batch_number}
                </Typography>
                <StyledChip
                  label={batch.batch_status}
                  status={batch.batch_status}
                  size="small"
                />
                <Badge badgeContent={batch.items.length} color="primary" />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                      <TableCell fontWeight={600}>Section</TableCell>
                      <TableCell fontWeight={600}>Item</TableCell>
                      <TableCell fontWeight={600}>SKU</TableCell>
                      <TableCell fontWeight={600}>Category</TableCell>
                      <TableCell fontWeight={600} align="right">Quantity</TableCell>
                      <TableCell fontWeight={600}>Last Updated</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {batch.items
                      .sort((a, b) => a.section_order - b.section_order)
                      .map((item, index) => (
                        <TableRow key={`${item.item_id}-${item.section_id}-${index}`}>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {item.section_name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {item.item_name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {item.sku || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {item.category || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={600}>
                              {item.quantity.toLocaleString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {new Date(item.last_updated).toLocaleDateString()}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress sx={{ color: '#000000' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={fetchInventoryData}
          sx={{
            backgroundColor: '#000000',
            color: '#ffffff',
            '&:hover': { backgroundColor: '#333333' },
          }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  if (!inventoryData) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <InventoryIcon sx={{ fontSize: '4rem', color: '#e0e0e0', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          No inventory data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', backgroundColor: '#ffffff' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ p: 3 }}>
          <Box>
            <Typography variant="h4" fontWeight={700} color="#000000" gutterBottom>
              Plant Inventory
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {inventoryData.plant.name} - Inventory Distribution
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchInventoryData}
            sx={{
              borderColor: '#000000',
              color: '#000000',
              '&:hover': {
                backgroundColor: '#f5f5f5',
                borderColor: '#000000',
              },
            }}
          >
            Refresh
          </Button>
        </Box>
        
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            px: 3,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '1rem',
              color: '#757575',
              '&.Mui-selected': {
                color: '#000000',
                fontWeight: 600,
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#000000',
            },
          }}
        >
          <Tab label="Overview" value="overview" />
          <Tab label="By Sections" value="sections" />
          <Tab label="By Batches" value="batches" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 'overview' && renderOverviewTab()}
      {activeTab === 'sections' && renderSectionsTab()}
      {activeTab === 'batches' && renderBatchesTab()}
    </Box>
  );
};

export default ProductionPlantInventory; 